import React, { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { withTranslation } from 'react-i18next'
import i18n from 'i18n'
import Cookies from 'js-cookie'

import ROUTES from '@shared/lib/constants/routes'
import { LocalStorage } from '@shared/services/main/storage'
import { getCdnImageUrl } from '@shared/lib/helpers/cdn-helper'
import { platformLogout } from '../features/auth/api'
import { useAuth } from '../features/auth/AuthProvider'

/**
 * Single header component: one bar (logo + hamburger | nav+auth) and one slide-out (mobile links only).
 * Visibility is purely via CSS media (lg breakpoint); no duplicate DOM for mobile vs desktop.
 */
function Header() {
  const location = useLocation()
  const auth = useAuth()
  const [sideMenuIsActive, setSideMenuIsActive] = useState(false)
  const [lang, setLang] = useState('en')

  const user = auth.user || {}
  const enabledModules = Array.isArray(user.enabled_modules) ? user.enabled_modules : []
  const canAccessBetxt = enabledModules.includes('betxt')
  const betxtStandaloneUrl = ROUTES.BETXT_STANDALONE_URL
  const credit = auth.credit
  const isLoggedIn = useCallback(() => !!auth.user, [auth.user])
  const displayName = [user.firstname, user.lastname].filter(Boolean).join(' ').trim()
  const balanceText = credit != null ? (typeof credit === 'number' ? credit.toFixed(2) : String(credit)) : '0.00'

  const handleToggleSideNav = useCallback(() => {
    setSideMenuIsActive(prev => !prev)
  }, [])

  const handleChangeLanguage = useCallback(e => {
    const value = e.target.value
    setLang(value)
    i18n.changeLanguage(value)
    if (document.body.parentElement) document.body.parentElement.lang = value
  }, [])

  const resolveLang = useCallback(() => {
    if (localStorage.getItem('i18nextLng') === 'en-US') {
      const langCookie = Cookies.get('sportsbook_country_id')
      if (langCookie) handleChangeLanguage({ target: { value: langCookie.toLocaleLowerCase() } })
    } else {
      const currentLang = i18n.language
      setLang(currentLang)
      if (document.body.parentElement) document.body.parentElement.lang = currentLang
    }
  }, [handleChangeLanguage])

  const handleLogoutClick = useCallback(async () => {
    try {
      await platformLogout()
    } finally {
      auth.refreshAuth()
    }
  }, [auth])

  const toggleLoginForm = useCallback((form, e) => {
    if (e?.preventDefault) e.preventDefault()
    window.dispatchEvent(new CustomEvent('c7:auth:open', { detail: { mode: form === ROUTES.REGISTER ? 'signup' : 'login' } }))
  }, [])

  const closeMobileMenu = useCallback(() => {
    if (sideMenuIsActive) setSideMenuIsActive(false)
  }, [sideMenuIsActive])

  const isPathActive = useCallback(path => location.pathname.startsWith(path), [location.pathname])

  useEffect(() => {
    resolveLang()
  }, [resolveLang])

  useEffect(() => {
    const currentLang = i18n.language
    if (lang !== currentLang && currentLang !== 'en-US') setLang(currentLang)
  }, [i18n.language, lang])

  const navLinkClass = (path) =>
    `rd-nav-link flex items-center min-h-[52px] px-4 py-2 text-white hover:bg-[#0b8e36] transition-all duration-300 uppercase text-sm font-semibold tracking-[0.075em] leading-[1.2] ${isPathActive(path) ? 'bg-[#0b8e36]' : ''}`

  const navLinkClassDesktop = (path) =>
    `rd-nav-link px-4 py-2 xl:px-5 xl:py-3 rounded transition-all duration-300 ease-in-out text-white hover:bg-[#0b8e36] uppercase text-sm xl:text-base font-semibold tracking-[0.075em] leading-[1.2] whitespace-nowrap flex items-center ${isPathActive(path) ? 'bg-[#0b8e36]' : ''}`

  return (
    <header className="section page-header rd-navbar-dark">
      <div className="rd-navbar-wrap">
        <nav
          className="rd-navbar rd-navbar-classic rd-navbar-original rd-navbar--menu-transition-disabled rd-navbar-fixed"
          data-layout="rd-navbar-fixed"
          data-sm-layout="rd-navbar-fixed"
          data-md-layout="rd-navbar-fixed"
          data-lg-layout="rd-navbar-fixed"
          data-xl-layout="rd-navbar-static"
          data-xxl-layout="rd-navbar-static"
          data-lg-stick-up-offset="166px"
          data-xl-stick-up-offset="166px"
          data-xxl-stick-up-offset="166px"
          data-lg-stick-up="true"
          data-xl-stick-up="true"
          data-xxl-stick-up="true"
        >
          {/* Single bar: logo + (hamburger on mobile | nav+auth on desktop). One DOM, visibility via CSS. */}
          <div className="rd-navbar-panel flex justify-between items-center w-full">
            <div className="rd-navbar-brand flex-shrink-0">
              <a className="brand flex items-center" href="/">
                <img className="brand-logo-dark" src="/images/logo.png" alt="" width="106" height="41" />
                <img className="brand-logo-light" src="/images/logo.png" alt="" width="106" height="41" />
              </a>
            </div>

            <button
              type="button"
              className={`rd-navbar-toggle toggle-original lg:hidden ${sideMenuIsActive ? 'active' : ''}`}
              data-rd-navbar-toggle=".rd-navbar-main"
              onClick={handleToggleSideNav}
              aria-label="Toggle menu"
            >
              <span />
            </button>

            <div className="hidden lg:flex flex-1 justify-center rd-navbar-nav-desktop">
              <ul className="rd-navbar-nav flex list-none m-0 p-0 gap-2">
                <li className={isPathActive(ROUTES.SPORTS) ? 'rd-nav-item active' : 'rd-nav-item'}>
                  <Link className={navLinkClassDesktop(ROUTES.SPORTS)} to={ROUTES.SPORTS}>
                    <i className="fa-regular fa-futbol not-italic mr-2 xl:mr-3 xl:text-lg" /><span>{i18n.t('sports')}</span>
                  </Link>
                </li>
                <li className={isPathActive(ROUTES.VIRTUAL_SPORTS) ? 'rd-nav-item active' : 'rd-nav-item'}>
                  <Link className={navLinkClassDesktop(ROUTES.VIRTUAL_SPORTS)} to={ROUTES.VIRTUAL_SPORTS}>
                    <i className="fa-solid fa-trophy not-italic mr-2 xl:mr-3 xl:text-lg" /><span>{i18n.t('virtual_sports')}</span>
                  </Link>
                </li>
                <li className={isPathActive(ROUTES.CASINO) ? 'rd-nav-item active' : 'rd-nav-item'}>
                  <Link className={navLinkClassDesktop(ROUTES.CASINO)} to={ROUTES.CASINO}>
                    <i className="fa-solid fa-dice not-italic mr-2 xl:mr-3 xl:text-lg" /><span>{i18n.t('casino')}</span>
                  </Link>
                </li>
                <li className={isPathActive(ROUTES.POKER) ? 'rd-nav-item active' : 'rd-nav-item'}>
                  <Link className={navLinkClassDesktop(ROUTES.POKER)} to={ROUTES.POKER}>
                    <img
                      src={getCdnImageUrl('sports', 38)}
                      alt="poker"
                      className="not-italic mr-2 xl:mr-3 w-4 h-4 xl:w-5 xl:h-5 object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                      onError={e => {
                        e.target.style.display = 'none'
                        const icon = document.createElement('i')
                        icon.className = 'fa-solid fa-diamond not-italic mr-2 xl:mr-3 xl:text-lg'
                        e.target.parentNode.insertBefore(icon, e.target)
                      }}
                    />
                    <span>{i18n.t('poker')}</span>
                  </Link>
                </li>
                {canAccessBetxt && betxtStandaloneUrl ? (
                  // TODO: Replace standalone BETXT link with remote-entry integration when BETXT host embedding is ready.
                  <li className="rd-nav-item">
                    <a
                      className="rd-nav-link px-4 py-2 xl:px-5 xl:py-3 rounded transition-all duration-300 ease-in-out text-white hover:bg-[#0b8e36] uppercase text-sm xl:text-base font-semibold tracking-[0.075em] leading-[1.2] whitespace-nowrap flex items-center"
                      href={betxtStandaloneUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fa-solid fa-ticket not-italic mr-2 xl:mr-3 xl:text-lg" />
                      <span>BETXT</span>
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>

            <div className="hidden lg:flex flex-shrink-0 rd-navbar-auth-controls">
              <ul className="list-inline list-inline-bordered flex flex-wrap items-center gap-4">
                <li>
                  <select className="select select-inline select-inline-dark" value={lang} onChange={handleChangeLanguage}>
                    <option value="en">🇺🇸 en</option>
                    <option value="it">🇮🇹 it</option>
                    <option value="es">🇪🇸 es</option>
                  </select>
                </li>
                <li className="registration">
                  {isLoggedIn() ? <div /> : <Link className="badge badge-primary" to={ROUTES.REGISTER} onClick={e => toggleLoginForm(ROUTES.REGISTER, e)}>{i18n.t('register')}</Link>}
                </li>
                {isLoggedIn() ? (
                  <>
                    <li className="header-user-balance flex items-center gap-3">
                      <Link className="link link-icon link-icon-left link-classic flex items-center" to={ROUTES.PLAYER.PROFILE}>
                        <span className="icon fl-bigmug-line-user144" />
                        <span className="link-icon-text">{displayName || i18n.t('profile')}</span>
                      </Link>
                      <span className="header-balance" aria-label={i18n.t('balance')}>{balanceText}</span>
                    </li>
                    <li className="login">
                      <button type="button" className="link link-icon link-icon-left link-classic border-0 bg-transparent p-0 cursor-pointer" onClick={handleLogoutClick}>
                        <span className="icon fl-bigmug-line-login9" />
                        <span className="link-icon-text">{i18n.t('logout')}</span>
                      </button>
                    </li>
                  </>
                ) : (
                  <li className="login">
                    <Link className="link link-icon link-icon-left link-classic" to={ROUTES.LOGIN} onClick={e => toggleLoginForm(ROUTES.LOGIN, e)}>
                      <span className="icon fl-bigmug-line-login12" /><span className="link-icon-text">{i18n.t('log_in')}</span>
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Backdrop: click outside to close mobile menu. Only visible when menu is open, mobile only. */}
          {sideMenuIsActive && (
            <div
              className="header-menu-backdrop lg:hidden"
              onClick={closeMobileMenu}
              onKeyDown={e => e.key === 'Escape' && closeMobileMenu()}
              role="button"
              tabIndex={-1}
              aria-label={i18n.t('common.close') || 'Close menu'}
            />
          )}

          {/* Single slide-out: only mobile nav links (no duplicate bar). Below lg = overlay; lg+ = hidden by CSS. */}
          <div className={`rd-navbar-main ${sideMenuIsActive ? 'active' : ''}`}>
            <div className="rd-navbar-main-bottom rd-navbar-darker lg:hidden">
              <div className="rd-navbar-main-container container mx-auto px-4">
                <ul className="rd-navbar-nav">
                  <li className={isPathActive(ROUTES.SPORTS) ? 'rd-nav-item active' : 'rd-nav-item'}>
                    <Link className={navLinkClass(ROUTES.SPORTS)} to={ROUTES.SPORTS} onClick={closeMobileMenu}>
                      <i className="fa-regular fa-futbol not-italic mr-2" />{i18n.t('sports')}
                    </Link>
                  </li>
                  <li className={isPathActive(ROUTES.VIRTUAL_SPORTS) ? 'rd-nav-item active' : 'rd-nav-item'}>
                    <Link className={navLinkClass(ROUTES.VIRTUAL_SPORTS)} to={ROUTES.VIRTUAL_SPORTS} onClick={closeMobileMenu}>
                      <i className="fa-solid fa-trophy not-italic mr-2" />{i18n.t('virtual_sports')}
                    </Link>
                  </li>
                  <li className={isPathActive(ROUTES.CASINO) ? 'rd-nav-item active' : 'rd-nav-item'}>
                    <Link className={navLinkClass(ROUTES.CASINO)} to={ROUTES.CASINO} onClick={closeMobileMenu}>
                      <i className="fa-solid fa-dice not-italic mr-2" />{i18n.t('casino')}
                    </Link>
                  </li>
                  <li className={isPathActive(ROUTES.POKER) ? 'rd-nav-item active' : 'rd-nav-item'}>
                    <Link className={navLinkClass(ROUTES.POKER)} to={ROUTES.POKER} onClick={closeMobileMenu}>
                      <img
                        src={getCdnImageUrl('sports', 38)}
                        alt="poker"
                        className="not-italic mr-2 w-4 h-4 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                        onError={e => {
                          e.target.style.display = 'none'
                          const icon = document.createElement('i')
                          icon.className = 'fa-solid fa-diamond not-italic mr-2'
                          e.target.parentNode.insertBefore(icon, e.target)
                        }}
                      />
                      {i18n.t('poker')}
                    </Link>
                  </li>
                  {canAccessBetxt && betxtStandaloneUrl ? (
                    <li className="rd-nav-item">
                      <a
                        className="rd-nav-link flex items-center min-h-[52px] px-4 py-2 text-white hover:bg-[#0b8e36] transition-all duration-300 uppercase text-sm font-semibold tracking-[0.075em] leading-[1.2]"
                        href={betxtStandaloneUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={closeMobileMenu}
                      >
                        <i className="fa-solid fa-ticket not-italic mr-2" />
                        BETXT
                      </a>
                    </li>
                  ) : null}
                </ul>
                <div className="rd-navbar-mobile-auth py-5 border-t border-gray-700">
                  <ul className="list-inline list-inline-bordered flex flex-col gap-4">
                    <li>
                      <select className="select select-inline select-inline-dark w-full py-2 px-3" value={lang} onChange={handleChangeLanguage}>
                        <option value="en">🇺🇸 English</option>
                        <option value="it">🇮🇹 Italiano</option>
                        <option value="es">🇪🇸 Español</option>
                      </select>
                    </li>
                    <li className="registration">
                      {isLoggedIn() ? <div /> : <Link className="badge badge-primary block text-center py-2.5" to={ROUTES.REGISTER} onClick={e => { toggleLoginForm(ROUTES.REGISTER, e); closeMobileMenu() }}>{i18n.t('register')}</Link>}
                    </li>
                    {isLoggedIn() ? (
                      <>
                        <li className="header-user-balance flex flex-col gap-2 py-2.5">
                          <Link className="link link-icon link-icon-left link-classic flex items-center justify-center" to={ROUTES.PLAYER.PROFILE} onClick={closeMobileMenu}>
                            <span className="icon fl-bigmug-line-user144" />
                            <span className="link-icon-text">{displayName || i18n.t('profile')}</span>
                          </Link>
                          <span className="header-balance text-center" aria-label={i18n.t('balance')}>{balanceText}</span>
                        </li>
                        <li className="login">
                          <button type="button" className="link link-icon link-icon-left link-classic block text-center py-2.5 w-full border-0 bg-transparent cursor-pointer" onClick={() => { handleLogoutClick(); closeMobileMenu() }}>
                            <span className="icon fl-bigmug-line-login9" />
                            <span className="link-icon-text">{i18n.t('logout')}</span>
                          </button>
                        </li>
                      </>
                    ) : (
                      <li className="login">
                        <Link className="link link-icon link-icon-left link-classic block text-center py-2.5" to={ROUTES.LOGIN} onClick={e => { toggleLoginForm(ROUTES.LOGIN, e); closeMobileMenu() }}>
                          <span className="icon fl-bigmug-line-login12" /><span className="link-icon-text">{i18n.t('log_in')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  )
}

export default withTranslation()(Header)
